import React, {createContext, useContext, useEffect, useMemo, useRef, useState,} from "react";
import queryString from "query-string";
import {
    applyActionCode,
    checkActionCode,
    confirmPasswordReset as authConfirmPasswordReset,
    createUserWithEmailAndPassword,
    FacebookAuthProvider,
    getAdditionalUserInfo,
    getAuth,
    getRedirectResult,
    GithubAuthProvider,
    GoogleAuthProvider,
    onAuthStateChanged,
    sendEmailVerification,
    sendPasswordResetEmail as authSendPasswordResetEmail,
    signInWithEmailAndPassword,
    signInWithPopup,
    signInWithRedirect,
    signOut as authSignOut,
    TwitterAuthProvider,
    updateEmail as authUpdateEmail,
    updatePassword as authUpdatePassword,
    updateProfile as authUpdateProfile
} from "firebase/auth";
import {firebaseApp} from "./firebase";
import {createUser, updateUser, useUser} from "./db";
import {useRouter} from "next/router";
import PageLoader from "./../components/PageLoader";
import {getFriendlyPlanId} from "./prices";
import {apiRequest} from "./util";
import sessionInfo from "./sessionInfo";
import track, {identify} from "./frontend_analytics";
import Cookies from 'js-cookie'; // Import js-cookie
import {armReturn, disarmReturn, forgetWall, takeRedirecting} from "./authReturn";
// import analytics from "./analytics";

// Whether to merge extra user data from database into `auth.user`
const MERGE_DB_USER = true;
// Whether to send email verification on signup
const EMAIL_VERIFICATION = false;
// Whether to connect analytics session to `user.uid`
const ANALYTICS_IDENTIFY = true;

// Initialize Firebase auth
const auth = getAuth(firebaseApp);

// Settles once Firebase has finished starting up. A redirect started before then loses its pending flag to the
// start-up's own redirect check, so the reader comes back signed out (firebase-js-sdk #6827, fixed after 9.1).
const authReady = typeof window === "undefined" ? Promise.resolve() : new Promise((resolve) => {
    let unsubscribe = null;
    unsubscribe = onAuthStateChanged(auth, () => {
        resolve();
        if (unsubscribe) unsubscribe();
    });
});

// Create a `useAuth` hook and `AuthProvider` that enables
// any component to subscribe to auth and re-render when it changes.
const authContext = createContext();
export const useAuth = () => useContext(authContext);

// This should wrap the app in `src/pages/_app.js`
export function AuthProvider({children}) {
    const auth = useAuthProvider();
    return <authContext.Provider value={auth}>{children}</authContext.Provider>;
}

// Hook that creates the `auth` object and handles state
// This is called from `AuthProvider` above (extracted out for readability)
function useAuthProvider() {
    // Store auth user in state
    // `user` will be object, `null` (loading) or `false` (logged out)
    const [user, setUser] = useState(null);

    // Merge extra user data from the database
    // This means extra user data (such as payment plan) is available as part
    // of `auth.user` and doesn't need to be fetched separately. Convenient!
    let finalUser = useMergeExtraData(user, {enabled: MERGE_DB_USER});

    // Add custom fields and formatting to the `user` object
    finalUser = useFormatUser(finalUser);

    // Connect analytics session to user
    useIdentifyUser(finalUser, {enabled: ANALYTICS_IDENTIFY});

    // Handle response from auth functions (`signup`, `signin`, and `signinWithProvider`)
    const handleAuth = async (response) => {
        const {user} = response;
        const {isNewUser} = getAdditionalUserInfo(response);

        // Ensure Firebase user is ready before we continue
        await waitForFirebase(user.uid);
        forgetWall();

        // Create the user in the database if they are new
        if (isNewUser) {
            const payload = {email: user.email};
            payload["availableCredits"] = sessionInfo.freeCreditsQuota;
            payload['landingParams'] = JSON.parse(localStorage.getItem('landingParams')) || {};
            await createUser(user.uid, payload);
            payload.value = 0.2;
            payload.branch = process.env.BRANCH || null;
            track("mytales_signup", payload,
                'AW-805456643/prnLCKK50Y4YEIOWiYAD'
            );

            await apiRequest("merge-stripe-customer", "POST", {
                email: user.email,
            });
            // Send email verification if enabled
            if (EMAIL_VERIFICATION) {
                sendEmailVerification(auth.currentUser);
            }
        }

        // Update user in state, unless they signed out while this finished
        if (auth.currentUser && auth.currentUser.uid === user.uid) {
            setUser(user);
        }
        return user;
    };

    const signup = (email, password) => {
        return createUserWithEmailAndPassword(auth, email, password).then(
            handleAuth
        );
    };

    const signin = (email, password) => {
        return signInWithEmailAndPassword(auth, email, password).then(
            handleAuth
        );
    };


    // A sign-in that went to Google in the same tab and came back: `{user, provider}` or `{error}`, for AuthSocial to finish
    const [redirectResult, setRedirectResult] = useState(null);

    // Phones block a pop-up opened a moment after the tap (Firebase loads a helper first), so they go to Google
    // in this tab and come back; elsewhere a blocked pop-up falls back to the same
    const signinWithProvider = (name) => {
        const provider = authProviders.find((p) => p.name === name).get();
        const redirect = () => {
            armReturn(name);
            return authReady.then(() => signInWithRedirect(auth, provider)).catch((error) => {
                disarmReturn();
                throw error;
            });
        };
        if (isPhone()) {
            return redirect();
        }
        return signInWithPopup(auth, provider)
            .then(handleAuth)
            .catch((error) => {
                if (error.code === "auth/popup-blocked") {
                    return redirect();
                }
                throw error;
            });
    };

    // The result is for the page the sign-in came back to: dropped on leaving it or signing out, even if still on its way
    const router = useRouter();
    const redirectAttempt = useRef(0);
    const dropRedirectResult = () => {
        redirectAttempt.current += 1;
        setRedirectResult(null);
    };
    useEffect(() => {
        const drop = () => dropRedirectResult();
        router.events.on("routeChangeStart", drop);
        return () => router.events.off("routeChangeStart", drop);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const provider = takeRedirecting();
        if (!provider) return;
        const attempt = redirectAttempt.current;
        const current = () => attempt === redirectAttempt.current;
        getRedirectResult(auth)
            .then((response) => response && handleAuth(response))
            .then((user) => user && current() && auth.currentUser && setRedirectResult({user, provider}))
            .catch((error) => {
                console.log("sign-in on return from Google failed", error);
                if (current()) setRedirectResult({error});
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const signout = () => {
        return authSignOut(auth);
    };

    const sendPasswordResetEmail = (email) => {
        return authSendPasswordResetEmail(auth, email);
    };

    const confirmPasswordReset = (password, code) => {
        // Get code from query string object
        const resetCode = code || getFromQueryString("oobCode");
        return authConfirmPasswordReset(auth, resetCode, password);
    };

    const updatePassword = (password) => {
        return authUpdatePassword(auth.currentUser, password);
    };

    // Update auth user and persist data to database
    // Call this function instead of multiple auth/db update functions
    const updateProfile = async (data) => {
        const {email, name, picture} = data;

        // Update auth email
        if (email) {
            await authUpdateEmail(auth.currentUser, email);
        }

        // Update built-in auth profile fields
        // These fields are renamed in `useFormatUser`, so when updating we
        // need to make sure to use their original names (`displayName`, `photoURL`, etc)
        if (name || picture) {
            let fields = {};
            if (name) fields.displayName = name;
            if (picture) fields.photoURL = picture;
            await authUpdateProfile(auth.currentUser, fields);
        }

        // Persist all data to the database
        await updateUser(user.uid, data);

        // Update user in state
        setUser(auth.currentUser);
    };

    useEffect(() => {
        // Subscribe to user on mount
        let signedIn = false;
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            if (user) {
                setUser(user);
            } else {
                setUser(false);
                // an actual sign-out, not the signed-out state a page starts in
                if (signedIn) dropRedirectResult();
            }
            signedIn = !!user;
        });

        // Unsubscribe on cleanup
        return () => unsubscribe();
    }, []);

    const deleteUser = async () => {
        if (!auth.currentUser) {
            throw new Error("No user is currently authenticated.");
        }

        // The server cancels any Stripe subscription, then deletes the login
        const accessToken = await auth.currentUser.getIdToken();
        const response = await fetch("/api/delete-account", {
            method: "POST",
            headers: {"Content-Type": "application/json", Authorization: `Bearer ${accessToken}`},
        }).then((res) => res.json());
        if (response.status !== "success") {
            throw new Error(response.message || "Your account was not deleted. Please email mytales@hosstechnology.com.");
        }

        // Sign out locally and reset the user state to `false` (logged out)
        await authSignOut(auth);
        setUser(false);
    };

    return {
        user: finalUser,
        signup,
        signin,
        signinWithProvider,
        redirectResult,
        clearRedirectResult: () => setRedirectResult(null),
        signout,
        sendPasswordResetEmail,
        confirmPasswordReset,
        updatePassword,
        updateProfile,
        deleteUser
    };
}

function useFormatUser(user) {
    // Memoize so returned object has a stable identity
    return useMemo(() => {
        // Return if auth user is `null` (loading) or `false` (not authenticated)
        if (!user) return user;

        // Create an array of user's auth providers by id (["password", "google", etc])
        // Components can read this to prompt user to re-auth with the correct provider
        const providers = user.providerData.map(({providerId}) => {
            return authProviders.find((p) => p.id === providerId).name;
        });

        return {
            // Include full auth user data
            ...user,
            // Alter the names of some fields
            name: user.displayName,
            picture: user.photoURL,
            // User's auth providers
            providers: providers,
            // Add `planId` (starter, pro, etc) if we have a `stripePriceId`
            ...(user.stripePriceId && {
                planId: getFriendlyPlanId(user.stripePriceId),
            }),
            // Add `planIsActive: true` if subscription status is active or trialing
            planIsActive: ["active", "trialing"].includes(
                user.stripeSubscriptionStatus
            ),
        };
    }, [user]);
}

function useMergeExtraData(user, {enabled}) {
    // Get extra user data from database
    const {data, status, error} = useUser(enabled && user && user.uid);

    // Memoize so returned object has a stable identity
    return useMemo(() => {
        // If disabled or no auth user (yet) then just return
        if (!enabled || !user) return user;

        switch (status) {
            case "success":
                // If successful, but `data` is `null`, that means user just signed up and the `createUser`
                // function hasn't populated the db yet. Return `null` to indicate auth is still loading.
                // The above call to `useUser` will re-render things once the data comes in.
                if (data === null) return null;
                // Return auth `user` merged with extra user `data`
                return {...user, ...data};
            case "error":
                // Uh oh.. Let's at least show a helpful error.
                throw new Error(`
          Error: ${error.message}
          This happened while attempting to fetch extra user data from the database
          to include with the authenticated user. Make sure the database is setup or
          disable merging extra user data by setting MERGE_DB_USER to false.
        `);
            default:
                // We have an `idle` or `loading` status so return `null`
                // to indicate that auth is still loading.
                return null;
        }
    }, [user, enabled, data, status, error]);
}

// Connect analytics session to current user
function useIdentifyUser(user, {enabled}) {
    useEffect(() => {

        if (enabled && user) {
            user.mytales = true;
            user.branch = process.env.BRANCH || null;
            identify(user.uid, user);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);
}

// A Higher Order Component for requiring authentication
export const requireAuth = (Component) => {
    return function RequireAuthHOC(props) {
        // Get authenticated user

        const auth = useAuth();
        const router = useRouter();


        useEffect(() => {
            // Redirect if not signed in
            if (auth.user === false) {
                router.replace("/auth/signup");
            }
        }, [auth]);


        // Show loading indicator
        // We're either loading (user is `null`) or about to redirect from above `useEffect` (user is `false`)
        if (!auth.user) {
            return <PageLoader/>;
        }

        // Render component now that we have user
        return <Component {...props} />;
    };
};

// Handle Firebase email link for reverting to original email after an email change
export const handleRecoverEmail = async (code) => {
    // Check that action code is valid
    const info = await checkActionCode(auth, code);
    // Revert to original email by applying action code
    await applyActionCode(auth, code);
    // Send password reset email so user can change their password in the case
    // that someone else got into their account and changed their email.
    await authSendPasswordResetEmail(auth, info.data.email);
    // Return original email so it can be displayed by calling component
    return info.data.email;
};

// Handle Firebase email link for verifying email
export const handleVerifyEmail = (code) => {
    return applyActionCode(auth, code);
};

const authProviders = [
    {
        id: "password",
        name: "password",
    },
    {
        id: "google.com",
        name: "google",
        get: () => new GoogleAuthProvider(),
    },
    {
        id: "facebook.com",
        name: "facebook",
        get: () => {
            const provider = new FacebookAuthProvider();
            provider.setCustomParameters({display: "popup"});
            return provider;
        },
    },
    {
        id: "twitter.com",
        name: "twitter",
        get: () => new TwitterAuthProvider(),
    },
    {
        id: "github.com",
        name: "github",
        get: () => new GithubAuthProvider(),
    },
];

// Wait for Firebase user to be initialized before resolving promise
// and taking any further action (such as writing to the database)
const waitForFirebase = (uid) => {
    return new Promise((resolve) => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            // Ensure we have a user with expected `uid`
            if (user && user.uid === uid) {
                resolve(user); // Resolve promise
                unsubscribe(); // Prevent from firing again
            }
        });
    });
};

const isPhone = () => {
    if (typeof navigator === "undefined") return false;
    // iPads report themselves as Macs, but with a touch screen
    return /Android|iPhone|iPad|iPod|Mobi/i.test(navigator.userAgent) ||
        (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
};

const getFromQueryString = (key) => {
    return queryString.parse(window.location.search)[key];
};

function isUserLoggedIn(auth) {
    if (auth.user === false || auth.user === null) {
        return false
    }
    return true;
}

function isUserSubscribed(auth) {
    if (!isUserLoggedIn(auth)) {
        return false;
    }
    return "stripeCustomerId" in auth.user;
}

function calcUserCredits(auth) {
    if (!isUserLoggedIn(auth)) {
        return 0;
    }

    var credits = sessionInfo.freeCreditsQuota;
    if (isUserSubscribed(auth)) {
        credits = credits + 500;
    }
    return credits;
}

function calcUsedCredits(auth) {
    if (!isUserLoggedIn(auth)) {
        return 0;
    }
    const usedCredits = "usedCredits" in auth.user ? auth.user.usedCredits : 0;
    return usedCredits;
}

function calcUserRemainingCredits(auth) {
    if (!isUserLoggedIn(auth)) {
        return 0;
    }
    const usedCredits = calcUsedCredits(auth);

    return calcUserCredits(auth) - usedCredits;
}

export default authContext;
export {
    isUserSubscribed,
    calcUserRemainingCredits,
    calcUserCredits,
    calcUsedCredits,
};
